import { useMemo } from 'react'
import {
  ScheduleAssignment,
  addDays,
  endOfMonth,
  endOfWeek,
  formatDateISO,
  isWeekend,
  pickTextColor,
  startOfMonth,
  startOfWeek,
  WEEKDAYS_PL,
} from './utils'

interface Props {
  anchor: Date
  assignments: ScheduleAssignment[]
  currentUserId: number | null
  onEdit: (a: ScheduleAssignment) => void
  onAdd?: (defaultDate?: string) => void
}

export default function ScheduleMonthView({ anchor, assignments, currentUserId, onEdit, onAdd }: Props) {
  const monthStart = useMemo(() => startOfMonth(anchor), [anchor])
  const monthEnd = useMemo(() => endOfMonth(anchor), [anchor])
  const gridStart = useMemo(() => startOfWeek(monthStart), [monthStart])
  const gridEnd = useMemo(() => endOfWeek(monthEnd), [monthEnd])

  const days = useMemo(() => {
    const arr: Date[] = []
    let cur = gridStart
    while (cur.getTime() <= gridEnd.getTime()) {
      arr.push(cur)
      cur = addDays(cur, 1)
    }
    return arr
  }, [gridStart, gridEnd])

  const byDay = useMemo(() => {
    const map: Record<string, ScheduleAssignment[]> = {}
    for (const a of assignments) {
      if (!map[a.date]) map[a.date] = []
      map[a.date].push(a)
    }
    for (const k of Object.keys(map)) {
      map[k].sort((x, y) => (x.effective_start_time || '').localeCompare(y.effective_start_time || ''))
    }
    return map
  }, [assignments])

  const today = formatDateISO(new Date())
  const currentMonth = anchor.getMonth()

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
        {WEEKDAYS_PL.map(w => (
          <div key={w} className="px-2 py-1.5 text-xs font-semibold text-gray-700 border-r last:border-r-0 border-gray-200">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d, idx) => {
          const iso = formatDateISO(d)
          const items = byDay[iso] || []
          const inMonth = d.getMonth() === currentMonth
          const isToday = iso === today
          const visibleItems = items.slice(0, 3)
          const more = items.length - visibleItems.length

          return (
            <div
              key={iso}
              className={`min-h-[100px] p-1 border-r border-b last:border-r-0 border-gray-200 align-top ${
                isWeekend(d) ? 'bg-gray-50/70' : ''
              } ${!inMonth ? 'opacity-50' : ''} ${isToday ? 'bg-blue-50' : ''}`}
              style={{ gridColumn: `span 1` }}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs ${isToday ? 'font-bold text-blue-700' : 'text-gray-600'}`}>
                  {d.getDate()}
                </span>
                {onAdd && (
                  <button
                    onClick={() => onAdd(iso)}
                    className="text-[10px] text-gray-400 hover:text-blue-600"
                    title="Dodaj wpis"
                  >+</button>
                )}
              </div>
              <div className="space-y-0.5">
                {visibleItems.map(a => (
                  <ChipItem key={a.id} a={a} isMine={a.user_id === currentUserId} onEdit={onEdit} />
                ))}
                {more > 0 && (
                  <div className="text-[10px] text-gray-500">+{more} więcej…</div>
                )}
              </div>
            </div>
          )
        })}
      </div>
      {/* Po liczbie dni 35–42, indeks idx unused — wyciszamy lintera */}
      <div className="hidden">{days.length}</div>
    </div>
  )
}

function ChipItem({ a, isMine, onEdit }: { a: ScheduleAssignment, isMine: boolean, onEdit: (a: ScheduleAssignment) => void }) {
  const bg = a.effective_color || '#3b82f6'
  const fg = pickTextColor(bg)
  const isOnCall = a.effective_category === 'oncall'
  const isLeave = a.effective_category === 'leave'

  return (
    <button
      onClick={() => onEdit(a)}
      className={`block w-full text-left rounded px-1 py-0.5 truncate ${
        isOnCall ? 'border border-dashed' : ''
      } ${isMine ? 'ring-1 ring-yellow-400' : ''}`}
      style={{
        backgroundColor: isLeave ? `${bg}22` : bg,
        borderColor: bg,
        color: isLeave ? '#374151' : fg,
      }}
      title={`${a.user_name || a.user_username || ''} – ${a.effective_label}${a.note ? `\n${a.note}` : ''}`}
    >
      <span className="text-[10px] truncate">
        {a.user_name || a.user_username || `#${a.user_id}`} · {a.effective_label}
      </span>
    </button>
  )
}
